import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { addDays, eatDayOf, formatIsoDay, parseIsoDay } from '@wodi/domain';
import type { RosterShiftView, RosterUnitView, RosterView } from '@wodi/shared';
import {
  Button,
  Cell,
  Checkbox,
  Column,
  DateField,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  decideSwap,
  rosterKeys,
  rosterQuery,
  saveShifts,
  shiftsQuery,
  swapsQuery,
  unitsQuery,
} from '../api/roster.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { errorText, LoadFailed, NotAllowed, OfflineListNotice } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { toasts } from '../toasts.ts';
import {
  AddToRosterDialog,
  dayLabel,
  RemoveEntryDialog,
  SwapDialog,
  type EntryChoice,
} from './roster-dialogs.tsx';
import { LicenceWarning } from './words.tsx';

// The roster (docs/plans/phase3-operations-modules.md §4.3): a ward's or department's week,
// people by days, built from staff records. Managers add and remove entries, set shifts and
// decide swaps; everyone may ask to swap their own shift. Expired licences show as a passive
// tag beside the name.

const WEEK = 7;

function unitId(u: RosterUnitView) {
  return `${u.type}:${u.key}`;
}

function weekDays(start: string): string[] {
  const parsed = parseIsoDay(start);
  if (!parsed.ok) return [];
  return Array.from({ length: WEEK }, (_, i) => formatIsoDay(addDays(parsed.value, i)));
}

function Grid({
  roster,
  days,
  userId,
  manage,
  online,
  onEntry,
}: {
  roster: RosterView;
  days: string[];
  userId: string;
  manage: boolean;
  online: boolean;
  onEntry: (entry: EntryChoice) => void;
}) {
  const shiftName = (id: string) => roster.shifts.find((s) => s.id === id)?.name ?? '';
  return (
    <TableScroll>
      <Table flat aria-label="Roster">
        <TableHeader>
          <Column isRowHeader>Person</Column>
          {days.map((d) => (
            <Column key={d}>{dayLabel(d)}</Column>
          ))}
        </TableHeader>
        <TableBody
          items={roster.people.map((p) => ({ ...p, id: p.userId }))}
          renderEmptyState={() => 'Nobody is on this roster this week yet.'}
        >
          {(p) => (
            <Row id={p.userId}>
              {[
                <Cell key="person">
                  <span className={styles.itemMain}>
                    <span>{p.displayName}</span>
                    <LicenceWarning licence={p.licence} />
                  </span>
                </Cell>,
                ...days.map((d) => (
                  <Cell key={d}>
                    <span className={styles.tags}>
                      {roster.entries
                        .filter((e) => e.userId === p.userId && e.onDate === d)
                        .map((e) => {
                          const choice = {
                            id: e.id,
                            userId: e.userId,
                            onDate: e.onDate,
                            shiftName: shiftName(e.shiftId),
                            personName: p.displayName,
                          };
                          const actionable = manage || e.userId === userId;
                          return actionable ? (
                            <Button
                              key={e.id}
                              dense
                              variant="quiet"
                              isDisabled={!online}
                              aria-label={`${choice.shiftName}, ${p.displayName}, ${dayLabel(d)}`}
                              onPress={() => {
                                onEntry(choice);
                              }}
                            >
                              {choice.shiftName}
                            </Button>
                          ) : (
                            <span key={e.id}>{choice.shiftName}</span>
                          );
                        })}
                    </span>
                  </Cell>
                )),
              ]}
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function Swaps({ online }: { online: boolean }) {
  const queryClient = useQueryClient();
  const swaps = useQuery(swapsQuery);
  const decide = useMutation({
    mutationFn: (input: { id: string; approve: boolean }) =>
      decideSwap(input.id, input.approve, null),
    onSuccess: (_d, input) => {
      void queryClient.invalidateQueries({ queryKey: rosterKeys.all });
      toasts.show({ title: input.approve ? 'Swap approved' : 'Swap rejected' });
    },
  });
  return (
    <Section
      title="Swap requests"
      description="Waiting for someone who manages the roster, other than the person who asked."
    >
      {swaps.isPending ? <LoadingBlock label="Loading swap requests" rows={2} /> : null}
      {swaps.isError ? (
        <LoadFailed
          error={swaps.error}
          fallback="Swap requests couldn't be loaded. Try again."
          onRetry={() => void swaps.refetch()}
        />
      ) : null}
      {decide.isError ? (
        <Notice tone="error">{errorText(decide.error, 'Try again.')}</Notice>
      ) : null}
      {swaps.isSuccess && swaps.data.items.length === 0 ? (
        <p className={styles.meta}>No swap requests waiting.</p>
      ) : null}
      {swaps.isSuccess && swaps.data.items.length > 0 ? (
        <ul className={styles.list} aria-label="Swap requests">
          {swaps.data.items.map((s) => (
            <li key={s.id} className={styles.item}>
              <span>
                {s.requester.displayName}&apos;s {s.from.shiftName} on {dayLabel(s.from.onDate)} to{' '}
                {s.toUser.displayName}
                {s.to === null ? '' : `, for their ${s.to.shiftName} on ${dayLabel(s.to.onDate)}`}
              </span>
              {s.mayDecide ? (
                <span className={styles.actions}>
                  <Button
                    dense
                    isDisabled={!online}
                    onPress={() => {
                      decide.mutate({ id: s.id, approve: true });
                    }}
                  >
                    Approve swap
                  </Button>
                  <Button
                    dense
                    variant="quiet"
                    isDisabled={!online}
                    onPress={() => {
                      decide.mutate({ id: s.id, approve: false });
                    }}
                  >
                    Reject swap
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </Section>
  );
}

function Shifts({ shifts, online }: { shifts: RosterShiftView[]; online: boolean }) {
  const queryClient = useQueryClient();
  const [items, setItems] =
    useState<(Omit<RosterShiftView, 'id'> & { id: string | null })[]>(shifts);
  const save = useMutation({
    mutationFn: () => saveShifts({ items }),
    onSuccess: (next) => {
      queryClient.setQueryData(rosterKeys.shifts, next);
      void queryClient.invalidateQueries({ queryKey: rosterKeys.all });
      setItems(next.items);
      toasts.show({ title: 'Shifts saved' });
    },
  });
  const change = (i: number, patch: Partial<(typeof items)[number]>) => {
    setItems(items.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  };
  return (
    <Section
      title="Shifts"
      description="Times in East Africa Time. An end before the start runs past midnight."
    >
      <div className={styles.form}>
        {items.map((s, i) => (
          <div key={s.id ?? `new-${String(i)}`} className={styles.row}>
            <TextField
              label="Shift name"
              value={s.name}
              onChange={(v) => {
                change(i, { name: v });
              }}
              width="md"
            />
            <TextField
              label="Starts"
              value={s.startsAt}
              onChange={(v) => {
                change(i, { startsAt: v });
              }}
              width="sm"
            />
            <TextField
              label="Ends"
              value={s.endsAt}
              onChange={(v) => {
                change(i, { endsAt: v });
              }}
              width="sm"
            />
            <Checkbox
              isSelected={s.active}
              onChange={(v) => {
                change(i, { active: v });
              }}
            >
              In use
            </Checkbox>
          </div>
        ))}
        {save.isError ? (
          <Notice tone="error">
            {errorText(save.error, "The shifts weren't saved. Try again.")}
          </Notice>
        ) : null}
        <div className={styles.actions}>
          <Button
            onPress={() => {
              setItems([
                ...items,
                { id: null, name: '', startsAt: '07:00', endsAt: '19:00', active: true },
              ]);
            }}
          >
            Add shift
          </Button>
          <Button
            variant="primary"
            isDisabled={!online}
            isPending={save.isPending}
            pendingLabel="Saving…"
            onPress={() => {
              save.mutate();
            }}
          >
            Save shifts
          </Button>
        </div>
      </div>
    </Section>
  );
}

export function RosterPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const units = useQuery(unitsQuery);
  const shifts = useQuery(shiftsQuery);
  const [now] = useState(() => new Date());
  const [unit, setUnit] = useState<string | null>(null);
  const [start, setStart] = useState<string | null>(formatIsoDay(eatDayOf(now)));
  const [dialog, setDialog] = useState<
    { kind: 'add' } | { kind: 'entry'; entry: EntryChoice } | null
  >(null);
  const chosen = units.data?.items.find((u) => unitId(u) === unit) ?? units.data?.items[0];
  const days = weekDays(start ?? formatIsoDay(eatDayOf(now)));
  const range =
    chosen === undefined || days.length === 0
      ? null
      : { unitType: chosen.type, unit: chosen.key, from: days[0] ?? '', to: days[WEEK - 1] ?? '' };
  const roster = useQuery({
    ...rosterQuery(range ?? { unitType: 'ward', unit: '-', from: '', to: '' }),
    enabled: range !== null,
  });
  if (session === null || !can(session, 'roster.read'))
    return <NotAllowed title="Roster" what="see rosters" />;
  const manage = can(session, 'roster.manage');
  const move = (by: number) => {
    const parsed = parseIsoDay(days[0] ?? '');
    if (parsed.ok) setStart(formatIsoDay(addDays(parsed.value, by)));
  };
  return (
    <div className={styles.page}>
      <PageHeader
        title="Roster"
        description="Who works which shift, by ward or department."
        {...(manage && chosen !== undefined && roster.isSuccess
          ? {
              actions: (
                <Button
                  variant="primary"
                  isDisabled={!online}
                  onPress={() => {
                    setDialog({ kind: 'add' });
                  }}
                >
                  Add to roster
                </Button>
              ),
            }
          : {})}
      />
      <p className={styles.actions}>
        <Link to="/attendance">My attendance</Link>
        {can(session, 'attendance.read') ? (
          <Link to="/roster/attendance">Attendance against roster</Link>
        ) : null}
      </p>
      <OfflineListNotice online={online} />
      {units.isError ? (
        <LoadFailed
          error={units.error}
          fallback="Wards and departments couldn't be loaded. Try again."
          onRetry={() => void units.refetch()}
        />
      ) : null}
      {units.isSuccess && units.data.items.length === 0 ? (
        <Notice tone="info">
          No wards or departments yet. Set up wards in Settings, or record departments on staff
          records.
        </Notice>
      ) : null}
      {chosen === undefined ? null : (
        <Section
          title={`${chosen.name}, week from ${dayLabel(days[0] ?? '')}`}
          actions={
            <span className={styles.actions}>
              <Button
                dense
                onPress={() => {
                  move(-WEEK);
                }}
              >
                Previous week
              </Button>
              <Button
                dense
                onPress={() => {
                  move(WEEK);
                }}
              >
                Next week
              </Button>
            </span>
          }
        >
          <div className={styles.form}>
            <div className={styles.row}>
              <Select
                label="Ward or department"
                options={(units.data?.items ?? []).map((u) => ({
                  id: unitId(u),
                  label: u.type === 'ward' ? `${u.name} (ward)` : `${u.name} (department)`,
                }))}
                value={unitId(chosen)}
                onChange={(key) => {
                  setUnit(key === null ? null : String(key));
                }}
                width="lg"
              />
              <DateField label="Week from" value={start} onChange={setStart} />
            </div>
            {roster.isPending ? <LoadingBlock label="Loading the roster" rows={5} /> : null}
            {roster.isError ? (
              <LoadFailed
                error={roster.error}
                fallback="The roster couldn't be loaded. Try again."
                onRetry={() => void roster.refetch()}
              />
            ) : null}
            {roster.isSuccess ? (
              <Grid
                roster={roster.data}
                days={days}
                userId={session.user.id}
                manage={manage}
                online={online}
                onEntry={(entry) => {
                  setDialog({ kind: 'entry', entry });
                }}
              />
            ) : null}
          </div>
        </Section>
      )}
      <Swaps online={online} />
      {manage && shifts.isSuccess ? (
        <Shifts key={shifts.dataUpdatedAt} shifts={shifts.data.items} online={online} />
      ) : null}
      {dialog?.kind === 'add' && chosen !== undefined && roster.isSuccess ? (
        <AddToRosterDialog
          unit={chosen}
          roster={roster.data}
          days={days}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog?.kind === 'entry' && roster.isSuccess ? (
        manage && dialog.entry.userId !== session.user.id ? (
          <RemoveEntryDialog
            entry={dialog.entry}
            onClose={() => {
              setDialog(null);
            }}
          />
        ) : (
          <SwapDialog
            entry={dialog.entry}
            roster={roster.data}
            onClose={() => {
              setDialog(null);
            }}
          />
        )
      ) : null}
    </div>
  );
}
